import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

import type { ChoiceEnumCell } from '../utils/choice-enum';
import type { MethodRouteOperationType } from '../utils/method-route-operations';

/**
 * AdapterMethodRoute model from the backend.
 */
export interface MethodRoute {
  id: string;
  market_entity: string;
  market_entity_code: string;
  market_entity_name: string;
  operation_types: string[];
  /** Rich ChoiceEnum cells from backend (`operation_types_display`). */
  operation_types_display?: ChoiceEnumCell[];
  execution_provider: string;
  execution_provider_code: string;
  execution_provider_name: string;
  merchant: string | null;
  merchant_name: string | null;
  merchant_group: string | null;
  merchant_group_name: string | null;
  is_exclusion_route: boolean;
  priority: number;
  is_active: boolean;
  is_effective: boolean;
  webhook_url: string;
  maintenance_mode: boolean;
  effective_from: string | null;
  effective_until: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Grouped routes by entity for the routing page.
 */
export interface EntityRouteGroup {
  entityId: string;
  entityCode: string;
  entityName: string;
  routes: MethodRoute[];
}

/**
 * Paginated list response.
 */
export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Accept a raw catalog array or a paginated `{results}` / `{data}` envelope. */
export function unwrapMethodRouteOperationTypes(body: unknown): MethodRouteOperationType[] {
  if (Array.isArray(body)) {
    return body as MethodRouteOperationType[];
  }
  if (body && typeof body === 'object') {
    const envelope = body as Record<string, unknown>;
    if (Array.isArray(envelope['results'])) {
      return envelope['results'] as MethodRouteOperationType[];
    }
    if (Array.isArray(envelope['data'])) {
      return envelope['data'] as MethodRouteOperationType[];
    }
  }
  return [];
}

/**
 * Service for managing Adapter Method Routes.
 */
@Injectable({
  providedIn: 'root',
})
export class RoutingService {
  private readonly api = inject(ApiService);
  private readonly basePath = 'manage/method-routes';

  /**
   * List all routes with optional filters.
   */
  list(params?: Record<string, string | number | boolean | undefined>): Observable<PaginatedResponse<MethodRoute>> {
    return this.api.get<PaginatedResponse<MethodRoute>>(`${this.basePath}/`, params);
  }

  /**
   * Get a single route by ID.
   */
  get(id: string): Observable<MethodRoute> {
    return this.api.get<MethodRoute>(`${this.basePath}/${id}/`);
  }

  /**
   * Create a new route.
   */
  create(data: Partial<MethodRoute>): Observable<MethodRoute> {
    return this.api.post<MethodRoute>(`${this.basePath}/`, data);
  }

  /**
   * Update a route (partial).
   */
  update(id: string, data: Partial<MethodRoute>): Observable<MethodRoute> {
    return this.api.patch<MethodRoute>(`${this.basePath}/${id}/`, data);
  }

  /**
   * Delete a route.
   */
  delete(id: string): Observable<void> {
    return this.api.delete(`${this.basePath}/${id}/`);
  }

  /**
   * Bulk create routes.
   */
  bulkCreate(routes: Partial<MethodRoute>[]): Observable<MethodRoute[]> {
    return this.api.post<MethodRoute[]>(`${this.basePath}/bulk-create/`, { routes });
  }

  /**
   * Full operation-type catalog (value, title, css, icon) for admin pickers.
   */
  listOperationTypes(): Observable<MethodRouteOperationType[]> {
    return this.api.get<unknown>(`${this.basePath}/operation-types/`).pipe(map(unwrapMethodRouteOperationTypes));
  }

  /**
   * Group routes by market entity.
   */
  groupByEntity(routes: MethodRoute[]): EntityRouteGroup[] {
    const groupMap = new Map<string, EntityRouteGroup>();

    for (const route of routes) {
      const key = route.market_entity;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          entityId: route.market_entity,
          entityCode: route.market_entity_code,
          entityName: route.market_entity_name,
          routes: [],
        });
      }
      groupMap.get(key)!.routes.push(route);
    }

    return Array.from(groupMap.values()).sort((a, b) => a.entityCode.localeCompare(b.entityCode));
  }
}
